<template>
  <div class="topic-detail-container pb-1">
    <div class="d-flex align-items-center mb-2 pb-1 border-bottom">
      <b-button variant="outline-secondary" size="sm" class="mr-2" @click="$router.back()">
        <b-icon icon="arrow-left"></b-icon> 返回
      </b-button>
      <h5 class="mb-0 font-weight-bold text-main">{{ editForm.name || '题材详情' }}</h5>
    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner label="Loading..."></b-spinner>
    </div>
    <div v-else>
      <div class="d-flex align-items-start mb-1 mt-2">
            <div :style="'white-space: pre-wrap; width: 100%;'" class="text-muted">
            {{ contentCollapsed ? getFirstItem(editForm.content) : editForm.content }}
            </div>

            <div class="ml-2 flex-shrink-0">
              <b-button size="sm" variant="outline-primary" @click="contentCollapsed = !contentCollapsed">
                {{ contentCollapsed ? '展开' : '合并' }}
              </b-button>
            </div>
          </div>
          <div class="text-muted small mt-1 mb-2 d-flex align-items-center flex-wrap" style="font-size: 11px;">
            <div class="flex-grow-1"></div>
            <span>声明：本文涉及的资讯、数据等内容来自网络公共信息，我们对以上信息的准确性和完整性不做任何保证，涉及的信息随时都可能会有新变化，仅作为学习交流之用。</span>
          </div>
          <div class="d-flex align-items-center mb-2 flex-wrap control-bar">
            <b-form-checkbox v-if="!isEditing" v-model="showQuotes" switch class="mr-3 mb-2" :disabled="loadingStocks" @change="onToggleQuotes">行情</b-form-checkbox>
            <b-spinner small v-if="loadingStocks" class="mb-2 mr-2"></b-spinner>
            <b-button-group size="sm" class="mb-2 mr-2">
              <b-button :variant="viewMode==='table'?'primary':'outline-secondary'" @click="switchToTableMode">表格</b-button>
              <b-button :variant="viewMode==='mindmap'?'primary':'outline-secondary'" @click="switchToMindMap">思维导图</b-button>
              <b-button :variant="viewMode==='kline'?'primary':'outline-secondary'" @click="switchToKlineMode">K线图</b-button>
              <b-button v-if="isEditing" :variant="viewMode==='json'?'primary':'outline-secondary'" @click="switchToJsonMode">JSON</b-button>
            </b-button-group>
            
            <div class="d-flex align-items-center mb-2">
              <b-button v-if="viewMode==='mindmap'" size="sm" variant="outline-secondary" class="mr-1" @click="enterMindMapFullscreen">全屏</b-button>
              <b-button v-if="viewMode==='mindmap'" size="sm" variant="outline-primary" class="mr-1" @click="downloadMindMap">下载图片</b-button>
              
              <b-button v-if="viewMode==='table' && !isEditing" size="sm" variant="outline-primary" class="mr-1" @click="downloadTableImage">下载图</b-button>
              <b-button v-if="viewMode==='table' && !isEditing" size="sm" variant="outline-primary" class="" @click="downloadTableCsv">下载表</b-button>
            </div>

            <div class="w-100 d-md-none"></div>

            <span class="text-muted small mb-2" style="font-size: 10px; line-height: 1.2;">
              <b-icon icon="info-circle" variant="info" class="mr-1"></b-icon>行情支持显示涨幅；导图支持全屏、缩放、下载。
            </span>
          </div>

          <div v-if="viewMode==='mindmap'" ref="mindmapContainer" class="mindmap-container rounded" style="height: 80vh; min-height: 600px; border: 1px solid var(--border-color);">
             <v-chart ref="mindmapChart" :key="mindMapStyle + mindMapCurve + mindMapSymbol + theme" :options="mindMapOption" :update-options="{ notMerge: true }" :theme="theme==='dark'?'site-dark':''" autoresize style="width:100%; height:100%;" />
          </div>

          <div v-show="viewMode === 'kline'" class="kline-container rounded border p-2 bg-white" style="position: relative; height: 70vh; min-height: 500px;">
            <!-- Subscription Overlay -->
            <div v-if="requireSubscription" class="subscription-overlay">
              <div class="overlay-card">
              <b-icon icon="lock" font-scale="3" class="mb-3"></b-icon>
              <h5>该功能属于高级付费模块</h5>
              <p>解锁【题材表格VIP】模块，深度分析历史题材走势、情绪周期与个股演变。</p>
              <div class="instruction-link mb-3">
                <a href="https://mp.weixin.qq.com/s/BV6xPZFO0O6-74lATXH5og" target="_blank">
                  <b-icon icon="question-circle" class="mr-1"></b-icon>题材表格VIP使用说明
                </a>
              </div>
              <b-button variant="primary" class="px-4" @click="$router.push({ path: '/subscription', query: { module: 'topic_kline' }})">前往解锁</b-button>
            </div>
            </div>

            <div class="d-flex align-items-center mb-2 flex-wrap">
              <b-form-checkbox v-if="isAdmin" v-model="showIntervalGain" switch class="mr-3 mb-2" @change="onToggleIndicator('showIntervalGain')">涨幅</b-form-checkbox>
              <b-form-checkbox v-model="showUpLimit" switch class="mr-3 mb-2" @change="onToggleIndicator('showUpLimit')">涨停</b-form-checkbox>
              <b-form-checkbox v-model="showDownLimit" switch class="mr-3 mb-2" @change="onToggleIndicator('showDownLimit')">跌停</b-form-checkbox>
              <b-form-checkbox v-model="showMorningLeader" switch class="mr-3 mb-2" @change="onToggleIndicator('showMorningLeader')">早盘领涨</b-form-checkbox>
              
              <div class="d-flex align-items-center mb-2 mr-3">
                <span class="text-muted small mr-2">列表位置:</span>
                <b-form-radio-group
                  v-model="stockLabelPosition"
                  :options="[
                    { text: '上方', value: 'above' },
                    { text: '下方', value: 'below' }
                  ]"
                  buttons
                  button-variant="outline-secondary"
                  size="sm"
                  @change="onStockLabelPositionChange"
                ></b-form-radio-group>
              </div>

              <div class="d-flex align-items-center mb-2 mr-3">
                <span class="text-muted small mr-2">标签样式:</span>
                <b-form-radio-group
                  v-model="klineLabelMode"
                  :options="[
                    { text: '完整', value: 'all' },
                    { text: '精简', value: 'compact' },
                    { text: '隐藏', value: 'none' }
                  ]"
                  buttons
                  button-variant="outline-secondary"
                  size="sm"
                  @change="onStockLabelModeChange"
                ></b-form-radio-group>
              </div>

              <div class="d-flex align-items-center mb-2 mr-3" v-if="klineLabelMode !== 'none'">
                <span class="text-muted small mr-2">每股数量:</span>
                <b-form-select
                  v-model="klineLabelLimit"
                  :options="[
                    { text: '最多3个', value: 3 },
                    { text: '最多5个', value: 5 },
                    { text: '最多8个', value: 8 },
                    { text: '无限制', value: 999 }
                  ]"
                  size="sm"
                  style="width: 100px; height: 31px; font-size: 13px;"
                  @change="onStockLabelLimitChange"
                ></b-form-select>
              </div>

              <b-form-checkbox v-model="showStockComparison" switch class="mr-3 mb-2" @change="onToggleStockComparison">个股叠加</b-form-checkbox>
              
              <div v-if="showStockComparison" class="d-flex align-items-center mb-2 mr-3">
                <span class="text-muted small mr-2">叠加数量:</span>
                <b-form-select
                  v-model="comparisonStockLimit"
                  :options="[
                    { text: '最多5个', value: 5 },
                    { text: '最多10个', value: 10 },
                    { text: '最多15个', value: 15 },
                    { text: '最多20个', value: 20 },
                    { text: '无限制', value: 999 }
                  ]"
                  size="sm"
                  style="width: 100px; height: 31px; font-size: 13px;"
                  @change="onComparisonStockLimitChange"
                ></b-form-select>
              </div>

              <div v-if="showStockComparison" class="d-flex align-items-center mb-2 mr-3">
                <span class="text-muted small mr-2">基准日期:</span>
                <b-form-select
                  v-model="comparisonBaseDate"
                  :options="baseDateOptions"
                  size="sm"
                  style="width: 120px; height: 31px; font-size: 13px;"
                  @change="onComparisonBaseDateChange"
                ></b-form-select>
                <b-button-group size="sm" class="ml-2">
                  <b-button variant="outline-primary" @click="setComparisonBaseRange('1w')" title="最近一周">1周</b-button>
                  <b-button variant="outline-primary" @click="setComparisonBaseRange('1m')" title="最近一月">1月</b-button>
                  <b-button variant="outline-primary" @click="setComparisonBaseRange('3m')" title="最近三月">3月</b-button>
                  <b-button variant="outline-primary" @click="setComparisonBaseRange('6m')" title="最近半年">半年</b-button>
                  <b-button variant="outline-primary" @click="setComparisonBaseRange('1y')" title="最近一年">1年</b-button>
                  <b-button variant="outline-primary" @click="setComparisonBaseRange('all')" title="全部题材区间">全部</b-button>
                </b-button-group>
              </div>

              <b-form-checkbox v-if="showStockComparison" v-model="showComparisonTooltip" switch class="mr-3 mb-2" @change="generateKlineOption">显示提示</b-form-checkbox>

              <div class="flex-grow-1"></div>
              <b-button v-if="canEdit" size="sm" variant="outline-danger" class="mb-2" @click="syncKlineData" :disabled="syncingKline">
                <b-spinner small v-if="syncingKline" class="mr-1"></b-spinner>更新K线数据
              </b-button>
            </div>
            <v-chart v-if="klineData.length > 0" :options="klineOption" :class="{ 'chart-blurred': requireSubscription }" :theme="theme==='dark'?'site-dark':''" autoresize @click="onChartClick" style="width:100%; height:calc(100% - 40px);" />
            <div v-else-if="loadingKline" class="text-center py-5">
              <b-spinner label="Loading..."></b-spinner>
              <div class="mt-2">正在获取K线数据...</div>
            </div>
            <div v-else class="text-center py-5 text-muted">
              <div>暂无K线数据</div>
              <b-button v-if="canEdit" size="sm" variant="primary" class="mt-3" @click="syncKlineData">立即生成数据</b-button>
            </div>
          </div>

          <div v-if="viewMode==='json'" class="mt-3">
            <b-form-textarea
              v-model="jsonStr"
              placeholder="请输入JSON数据..."
              rows="20"
              max-rows="50"
              @blur="syncJsonToRows"
            ></b-form-textarea>
            <div class="text-muted small mt-1">编辑JSON字符串，格式为对象数组。修改后自动同步到表格。</div>
            <b-alert :show="!!jsonError" variant="danger" class="mt-2">{{ jsonError }}</b-alert>
          </div>

          <div v-if="viewMode==='table' && !isEditing" class="table-responsive">
            <table class="table table-sm table-bordered wonder-table-view-small table-hover" :style="{ fontSize: isMobile ? '10px' : '13px' }">
              <thead class="thead-light">
                <tr>
                  <th class="col-cat" :style="{ fontSize: isMobile ? '10px' : '' }">一级大类</th>
                  <th class="col-cat" v-if="!isMobile && hasLevel2" :style="{ fontSize: isMobile ? '10px' : '' }">二级小类</th>
                  <th class="col-cat" v-if="!isMobile && hasLevel3" :style="{ fontSize: isMobile ? '10px' : '' }">三级细分</th>
                  <th class="col-stock" :style="{ fontSize: isMobile ? '10px' : '' }">个股</th>
                  <th class="col-code" v-if="showQuotes" :style="{ fontSize: isMobile ? '10px' : '' }">股票代码</th>
                  <th class="col-quote" v-if="showQuotes" :style="{ fontSize: isMobile ? '10px' : '' }">流通市值</th>
                  <th class="col-quote" v-if="showQuotes" :style="{ fontSize: isMobile ? '10px' : '' }">当前价格</th>
                  <th class="col-quote" v-if="showQuotes" :style="{ fontSize: isMobile ? '10px' : '' }">涨跌幅</th>
                  <th class="col-quote" v-if="showQuotes" :style="{ fontSize: isMobile ? '10px' : '' }">10日涨</th>
                  <th class="col-quote" v-if="showQuotes" :style="{ fontSize: isMobile ? '10px' : '' }">20日涨</th>
                  <th class="col-content" :style="{ fontSize: isMobile ? '10px' : '' }">相关性</th>
                  <th class="col-info" v-if="!isMobile && canEdit" :style="{ fontSize: isMobile ? '10px' : '' }">信息源</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rIdx) in mergedRows" :key="'mr-'+rIdx">
                  <td v-if="row._l1Show" :rowspan="row._l1Span" class="align-middle bg-light text-center"><strong>{{ row['一级大类'] }}</strong></td>
                  <td v-if="!isMobile && hasLevel2 && row._l2Show" :rowspan="row._l2Span" class="align-middle text-center"><strong>{{ row['二级小类'] }}</strong></td>
                  <td v-if="!isMobile && hasLevel3 && row._l3Show" :rowspan="row._l3Span" class="align-middle text-center"><strong>{{ row['三级细分'] }}</strong></td>
                  <td style="white-space: nowrap;" class="align-middle text-center cursor-pointer" @click.stop="openSidebar(row)"><strong>{{ row['个股'] }}</strong></td>
                  <td v-if="showQuotes" style="white-space: nowrap;" class="align-middle text-center">
                    <span class="cursor-pointer text-primary" @click.stop="openSidebar(row)">{{ row.symbol_code || row['股票代码'] || '' }}</span>
                  </td>
                  <td v-if="showQuotes" class="align-middle text-center">{{ formatYi(row['流通市值']) }}</td>
                  <td v-if="showQuotes" class="align-middle text-center">{{ row['最新价'] }}</td>
                  <td v-if="showQuotes" class="align-middle text-center"><span :style="[getRateStyle(row['涨跌幅']), {cursor: 'pointer'}]" @click.stop="openSidebar(row)">{{ row['涨跌幅'] }}<span v-if="row['涨跌幅'] !== undefined && row['涨跌幅'] !== null && row['涨跌幅'] !== ''">%</span></span></td>
                  <td v-if="showQuotes" class="align-middle text-center"><span :style="[getRateStyle(row['10日涨幅']), {cursor: 'pointer'}]" @click.stop="openSidebar(row)">{{ row['10日涨幅'] }}<span v-if="row['10日涨幅'] !== undefined && row['10日涨幅'] !== null && row['10日涨幅'] !== ''">%</span></span></td>
                  <td v-if="showQuotes" class="align-middle text-center"><span :style="[getRateStyle(row['20日涨幅']), {cursor: 'pointer'}]" @click.stop="openSidebar(row)">{{ row['20日涨幅'] }}<span v-if="row['20日涨幅'] !== undefined && row['20日涨幅'] !== null && row['20日涨幅'] !== ''">%</span></span></td>
                  <td class="col-content align-middle">{{ row['相关性'] }}</td>
                  <td v-if="!isMobile && canEdit" class="align-middle">{{ row['信息源'] }}</td>
                </tr>
                <tr v-if="!mergedRows.length">
                  <td :colspan="showQuotes ? (isMobile ? 9 : 12) : (isMobile ? 3 : 6)" class="text-center text-muted py-4">
                    暂无内容
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div v-if="viewMode==='table' && !isEditing" class="text-muted small mt-2 text-center" style="font-size: 10px;">
            声明：本文涉及的资讯、数据等内容来自网络公共信息，我们对以上信息的准确性和完整性不做任何保证，涉及的信息随时都可能会有新变化，仅作为学习交流之用。
          </div>

          <!-- Edit Mode Components -->
          <b-table v-if="viewMode==='table' && isEditing" small striped hover responsive bordered :items="editForm.rows" :fields="modalTableFields" :style="{ fontSize: isMobile ? '8px' : '' }" class="mt-3">
            <template #cell(流通市值)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['流通市值']" size="sm"></b-form-input>
              <span v-else>{{ item['流通市值'] }}</span>
            </template>
            <template #cell(最新价)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['最新价']" size="sm"></b-form-input>
              <span v-else>{{ item['最新价'] }}</span>
            </template>
            <template #cell(一级大类)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['一级大类']" size="sm"></b-form-input>
              <span v-else class="font-weight-bold">{{ item['一级大类'] }}</span>
            </template>
            <template #cell(二级小类)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['二级小类']" size="sm"></b-form-input>
              <span v-else class="font-weight-bold">{{ item['二级小类'] }}</span>
            </template>
            <template #cell(三级细分)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['三级细分']" size="sm"></b-form-input>
              <span v-else class="font-weight-bold">{{ item['三级细分'] }}</span>
            </template>
            <template #cell(个股)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['个股']" size="sm"></b-form-input>
              <span v-else class="font-weight-bold">{{ item['个股'] }}</span>
            </template>
            <template #cell(涨跌幅)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['涨跌幅']" size="sm"></b-form-input>
              <span v-else :style="[getRateStyle(item['涨跌幅']), {cursor: 'pointer'}]" @click.stop="openSidebar(item)">{{ item['涨跌幅'] }}<span v-if="item['涨跌幅'] !== undefined && item['涨跌幅'] !== null && item['涨跌幅'] !== ''">%</span></span>
            </template>
            <template #cell(10日涨幅)="{ item }">
              <span :style="getRateStyle(item['10日涨幅'])">{{ item['10日涨幅'] }}<span v-if="item['10日涨幅'] !== undefined && item['10日涨幅'] !== null && item['10日涨幅'] !== ''">%</span></span>
            </template>
            <template #cell(20日涨幅)="{ item }">
              <span :style="getRateStyle(item['20日涨幅'])">{{ item['20日涨幅'] }}<span v-if="item['20日涨幅'] !== undefined && item['20日涨幅'] !== null && item['20日涨幅'] !== ''">%</span></span>
            </template>
            <template #cell(相关性)="{ item }">
              <b-form-input v-if="isEditing" v-model="item['相关性']" size="sm"></b-form-input>
              <div v-else style="white-space: pre-wrap; word-break: break-word;">{{ item['相关性'] }}</div>
            </template>
            <template #cell(信息源)="{ item }">
              <template v-if="canEdit">
                <b-form-input v-if="isEditing" v-model="item['信息源']" size="sm"></b-form-input>
                <span v-else>{{ item['信息源'] }}</span>
              </template>
            </template>
            <template #cell(actions)="{ index }">
              <b-button v-if="isEditing" size="sm" variant="danger" class="mr-1" @click="editForm.rows.splice(index, 1)" title="删除">
                <b-icon icon="trash"></b-icon>
              </b-button>
              <b-button v-if="isEditing" size="sm" variant="success" @click="addRow(index)" title="新增">
                <b-icon icon="plus"></b-icon>
              </b-button>
            </template>
          </b-table>
          
          <div v-if="isEditing" class="mt-4 p-3 bg-light rounded border">
            <div v-if="editForm.image_url" class="mb-3 text-center">
              <div class="small text-muted mb-1">原始参考图</div>
              <img :src="editForm.image_url" class="img-fluid rounded border" style="max-height: 400px;" />
            </div>
            <h5 class="mb-3">编辑基础信息</h5>
            <b-form-group label="名称">
              <b-form-input v-model="editForm.name"></b-form-input>
            </b-form-group>
            <div class="row">
              <div class="col-6">
                <b-form-group label="创建时间">
                  <b-form-input v-model="editForm.created_time" placeholder="YYYY-MM-DD HH:mm:ss"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-6">
                <b-form-group label="更改时间">
                  <b-form-input v-model="editForm.updated_time" placeholder="YYYY-MM-DD HH:mm:ss"></b-form-input>
                </b-form-group>
              </div>
            </div>
            <b-form-group label="内容">
              <b-form-textarea v-model="editForm.content" rows="10"></b-form-textarea>
            </b-form-group>
            <div class="row">
              <div class="col-6">
                <b-form-group label="来源">
                  <b-form-select v-model="editForm.source" :options="sourceOptions"></b-form-select>
                </b-form-group>
              </div>
              <div class="col-6">
                <b-form-group label="文章ID">
                  <b-input-group>
                    <b-form-input v-model="editForm.article_id" placeholder="来源文章ID"></b-form-input>
                    <b-input-group-append>
                      <b-button variant="outline-info" size="sm" :disabled="!editForm.article_id || merging" @click="mergeDeepSeek" title="使用DeepSeek融合最新内容">
                        <b-spinner small v-if="merging"></b-spinner>
                        <b-icon v-else icon="cpu"></b-icon> AI融合
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </div>
            </div>
            <b-button size="sm" variant="primary" :disabled="submitting" @click="updateTable">
              <b-spinner small v-if="submitting" class="mr-1"></b-spinner>保存修改
            </b-button>
            
            <div class="mt-4 border-top pt-3">
              <h6 class="mb-3">重新识别覆盖内容</h6>
              <b-form-group label="选择图片">
                <b-form-file v-model="editForm.image" accept="image/*" @input="onEditImageChange" placeholder="选择或拖拽图片..."></b-form-file>
              </b-form-group>
              <div v-if="editImagePreview" class="mb-3 text-center bg-white p-2 border rounded">
                <img :src="editImagePreview" alt="预览" style="max-height: 200px; max-width: 100%; object-fit: contain;" />
              </div>
              <div class="d-flex">
                <b-button size="sm" variant="info" class="mr-2" :disabled="submitting || !editForm.image" @click="recognizeEditImage('overwrite')">识别并覆盖</b-button>
                <b-button size="sm" variant="success" :disabled="submitting || !editForm.image" @click="recognizeEditImage('append')">识别并追加</b-button>
              </div>
              <b-alert :show="editOcrMsg!==''" variant="info" class="mt-3 mb-0">{{ editOcrMsg }}</b-alert>
            </div>
          </div>
          <div v-if="isEditing && !canEdit" class="text-muted small mt-2">提示：没有编辑权限（需要 post 角色）。</div>
        </div>

    <StockInfoSidebar :symbol_code="sidebarSymbolCode" :symbol_name="sidebarSymbolName" :date_mark="sidebarDateMark" />
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import ECharts from 'vue-echarts'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'

export default {
  name: 'WonderTopicDetail',
  components: { 'v-chart': ECharts, StockInfoSidebar },
  data() {
    return {
      windowWidth: window.innerWidth,
      theme: 'light',
      loading: true,
      submitting: false,
      requireSubscription: false,
      
      editId: this.$route.params.id,
      editForm: { name: '', content: '', source: 1, article_id: '', rows: [], image: null, created_time: '', updated_time: '' },
      sourceOptions: [
        { value: 1, text: '韭研公社' },
        { value: 0, text: '其他' }
      ],
      editImagePreview: '',
      editOcrMsg: '',
      merging: false,
      
      isEditing: false, 
      viewMode: 'table',
      mindMapOption: {},
      mindMapStyle: 'LR',
      mindMapCurve: 'curve',
      mindMapSymbol: 'emptyCircle',
      
      loadingStocks: false,
      jsonStr: '',
      jsonError: '',
      lastQuoteTime: '',
      contentCollapsed: true,
      showQuotes: true,
      
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      sidebarDateMark: '',
      
      recognizedFields: [ { key:'一级大类', label:'一级大类' }, { key:'二级小类', label:'二级小类' }, { key:'三级细分', label:'三级细分' }, { key:'个股', label:'个股' }, { key:'相关性', label:'相关性' }, { key:'信息源', label:'信息源' }, { key:'actions', label:'操作' } ],
      
      klineData: [],
      klineOption: {},
      loadingKline: false,
      syncingKline: false,
      showIntervalGain: false,
      showUpLimit: true,
      showDownLimit: false,
      showMorningLeader: false,
      stockLabelPosition: localStorage.getItem('topic_kline_label_pos') || 'above',
      klineLabelMode: localStorage.getItem('topic_kline_label_mode') || 'compact',
      klineLabelLimit: parseInt(localStorage.getItem('topic_kline_label_limit') || '5', 10),
      showStockComparison: false,
      comparisonBaseDate: '',
      comparisonStockLimit: parseInt(localStorage.getItem('topic_comparison_stock_limit') || '10', 10),
      showComparisonTooltip: true,
      comparisonKlineData: {},
      comparisonStockNames: {},
      comparisonStockColors: {},
      selectedComparisonStocks: []
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768
    },
    hasLevel2() {
      const rows = this.editForm.rows || []
      return rows.some(r => r['二级小类'] && String(r['二级小类']).trim() !== '')
    },
    hasLevel3() {
      const rows = this.editForm.rows || []
      return rows.some(r => r['三级细分'] && String(r['三级细分']).trim() !== '')
    },
    canEdit() {
      try{
        const raw = localStorage.getItem('user_info')
        if(!raw) return false
        const u = JSON.parse(raw)
        const r = u && u.roles
        const arr = Array.isArray(r) ? r : String(r||'').split(',').map(s=>s.trim()).filter(Boolean)
        return arr.includes('post')
      }catch(e){ return false }
    },
    isAdmin() {
      try {
        const raw = localStorage.getItem('user_info')
        if (raw) {
          const u = JSON.parse(raw)
          const r = u && u.roles
          const arr = Array.isArray(r) ? r : String(r||'').split(',').map(s=>s.trim()).filter(Boolean)
          if (arr.includes('admin')) return true
        }
      } catch (e) {
        console.warn('isAdmin check raw error', e)
      }
      try {
        const token = localStorage.getItem('token')
        if (token) {
          const base64Url = token.split('.')[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          }).join(''))
          const decoded = JSON.parse(jsonPayload)
          const roles = decoded.roles || ''
          const arr = Array.isArray(roles) ? roles : String(roles || '').split(',').map(s=>s.trim()).filter(Boolean)
          if (arr.includes('admin')) return true
        }
      } catch (e) {
        console.warn('isAdmin check token error', e)
      }
      return false
    },
    modalTableFields(){
      try{
        const base = Array.isArray(this.recognizedFields) ? this.recognizedFields : []
        let fields = base.slice()
        if(!this.showQuotes){
          fields = fields.filter(f => f.key !== '流通市值' && f.key !== '最新价' && f.key !== '涨跌幅' && f.key !== '10日涨幅' && f.key !== '20日涨幅')
        } else {
          const hasCap = fields.some(f => f.key === '流通市值')
          const hasPrice = fields.some(f => f.key === '最新价')
          const hasRate = fields.some(f => f.key === '涨跌幅')
          const hasRate10d = fields.some(f => f.key === '10日涨幅')
          const hasRate20d = fields.some(f => f.key === '20日涨幅')
          if(!hasCap) fields.splice(4, 0, { key:'流通市值', label:'流通市值' })
          if(!hasPrice) fields.splice(5, 0, { key:'最新价', label:'当前价格' })
          if(!hasRate) fields.splice(6, 0, { key:'涨跌幅', label:'涨跌幅' })
          if(!hasRate10d) fields.splice(7, 0, { key:'10日涨幅', label:'10日涨幅' })
          if(!hasRate20d) fields.splice(8, 0, { key:'20日涨幅', label:'20日涨幅' })
          const hasCode = fields.some(f => f.key === 'symbol_code')
          if(!hasCode) fields.splice(4, 0, { key:'symbol_code', label:'股票代码' })
        }
        if(this.isEditing) return fields
        let filtered = fields.filter(f => f.key !== 'actions')
        if (!this.canEdit) {
          filtered = filtered.filter(f => f.key !== '信息源')
        }
        return filtered
      }catch(e){
        return (this.recognizedFields || []).filter(f => f.key !== 'actions')
      }
    },
    mergedRows(){
      try{
        const src = Array.isArray(this.editForm.rows) ? this.editForm.rows : []
        const rows = src.map(r => ({
          ...r,
          _l1Show: true, _l1Span: 1,
          _l2Show: true, _l2Span: 1,
          _l3Show: true, _l3Span: 1
        }))
        const n = rows.length
        let i = 0
        while (i < n) {
          const l1 = (rows[i]['一级大类'] || '').trim()
          let j = i + 1
          while (j < n && (rows[j]['一级大类'] || '').trim() === l1) j++
          rows[i]._l1Show = true
          rows[i]._l1Span = j - i
          for (let k = i + 1; k < j; k++) { rows[k]._l1Show = false }
          let k = i
          while (k < j) {
            const l2 = (rows[k]['二级小类'] || '').trim()
            let m = k + 1
            while (m < j && (rows[m]['二级小类'] || '').trim() === l2) m++
            rows[k]._l2Show = true
            rows[k]._l2Span = m - k
            for (let t = k + 1; t < m; t++) { rows[t]._l2Show = false }
            let t = k
            while (t < m) {
              const l3 = (rows[t]['三级细分'] || '').trim()
              let u = t + 1
              while (u < m && (rows[u]['三级细分'] || '').trim() === l3) u++
              rows[t]._l3Show = true
              rows[t]._l3Span = u - t
              for (let v = t + 1; v < u; v++) { rows[v]._l3Show = false }
              t = u
            }
            k = m
          }
          i = j
        }
        return rows
      }catch(e){
        return this.editForm.rows || []
      }
    },
    stockNameCodeMap() {
      const map = {}
      ;(this.editForm.rows || []).forEach(row => {
        const name = (row['个股'] || '').trim()
        const code = row.symbol_code || row['股票代码']
        if (name && code) {
          map[name] = code
        }
      })
      return map
    },
    sortedComparisonCodes() {
      const codes = Object.keys(this.comparisonKlineData || {})
      return codes.sort((a, b) => {
        const nameA = this.getStockName(a)
        const nameB = this.getStockName(b)
        return nameA.localeCompare(nameB)
      })
    },
    baseDateOptions() {
      if (!this.klineData || this.klineData.length === 0) return []
      return this.klineData.map(d => ({ text: d.trade_date, value: d.trade_date }))
    }
  },
  mounted() {
    this.checkEditMode()
    this.fetchData()
    window.addEventListener('resize', this.onResize)
    window.addEventListener('paste', this.handlePaste)
    
    try { const t = localStorage.getItem('site.theme'); if(t) { this.theme = t } } catch(e) { console.warn('theme read failed', e) }
    this.onThemeChanged = (e) => { 
        const theme = e && e.detail && e.detail.theme ? e.detail.theme : (localStorage.getItem('site.theme')||'light'); 
        this.theme = theme; 
        if (this.viewMode === 'mindmap') {
            this.generateMindMap() 
        }
        if (this.klineData && this.klineData.length > 0) {
            this.generateKlineOption()
        }
    }
    window.addEventListener('site-theme-changed', this.onThemeChanged)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('paste', this.handlePaste)
    window.removeEventListener('site-theme-changed', this.onThemeChanged)
  },
  methods: {
    checkEditMode() {
        if (this.$route.query.edit === '1' && this.canEdit) {
            this.isEditing = true
            this.showQuotes = false
        }
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },
    formatYi(val){
      if (val === undefined || val === null || val === '') return ''
      const n = parseFloat(val)
      if (isNaN(n)) return String(val)
      const yi = n / 100000000
      return yi.toFixed(2) + '亿'
    },
    getFirstItem(content) {
      if (!content) return '';
      let c = String(content);
      const regex = /(^|\n)\s*2[、.]/;
      const match = c.match(regex);
      if (match) {
          return c.substring(0, match.index).trim();
      }
      const newlineIdx = c.indexOf('\n');
      if (newlineIdx > -1) {
          return c.substring(0, newlineIdx).trim();
      }
      return c;
    },
    fetchData() {
        this.loading = true
        this.klineData = []
        this.comparisonKlineData = {}
        this.comparisonBaseDate = ''
        this.comparisonStockNames = {}
        request_zzquant({ url: '/v3/topic/table/' + this.editId, method: 'GET' })
          .then(res => {
            const topic = res && res.data && res.data.data ? res.data.data : null
            if (topic && topic.id) {
                this.populateForm(topic)
                sessionStorage.setItem('current_topic', JSON.stringify(topic))
            } else {
                this.$bvToast.toast('未找到该题材或已被删除', { variant: 'danger', solid: true })
            }
          })
          .catch(e => {
            console.error(e)
            this.$bvToast.toast('加载失败', { variant: 'danger', solid: true })
          })
          .finally(() => {
            this.loading = false
          })
    },
    populateForm(item) {
        this.editForm = { 
            name: item.name || '', 
            content: item.content || '', 
            source: item.source || '',
            article_id: item.article_id || '',
            rows: item.rows || [], 
            image: null,
            updated_time: item.updated_time || '',
            created_time: item.created_time || ''
        }
        document.title = this.editForm.name + ' - 自在量化'
        if (this.showQuotes) {
            this.queryStockReturns()
        }
    },
    handlePaste(event){
      if (!this.isEditing) return
      const items = (event.clipboardData || event.originalEvent.clipboardData).items
      for (let index in items) {
        const item = items[index]
        if (item.kind === 'file' && item.type.indexOf('image/') !== -1) {
          const blob = item.getAsFile()
          this.editForm.image = blob
          this.onEditImageChange(blob)
          event.preventDefault()
          return
        }
      }
    },
    onEditImageChange(file){ 
        if (file) { 
            try { this.editImagePreview = URL.createObjectURL(file) } catch(e){ this.editImagePreview='' } 
        } else { 
            this.editImagePreview='' 
        } 
    },
    compressImage(file){
      return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (e) => {
              const img = new Image()
              img.src = e.target.result
              img.onload = () => {
                  const canvas = document.createElement('canvas')
                  let width = img.width
                  let height = img.height
                  const MAX_DIM = 2048
                  if(width > MAX_DIM || height > MAX_DIM){
                      if(width > height){
                          height = Math.round(height * MAX_DIM / width)
                          width = MAX_DIM
                      } else {
                          width = Math.round(width * MAX_DIM / height)
                          height = MAX_DIM
                      }
                  }
                  canvas.width = width
                  canvas.height = height
                  const ctx = canvas.getContext('2d')
                  ctx.drawImage(img, 0, 0, width, height)
                  canvas.toBlob((blob) => {
                      if(blob){
                          const newFile = new File([blob], file.name || 'image.jpg', { type: 'image/jpeg', lastModified: Date.now() })
                          resolve(newFile)
                      } else {
                          reject(new Error('Canvas toBlob failed'))
                      }
                  }, 'image/jpeg', 0.7)
              }
              img.onerror = reject
          }
          reader.onerror = reject
      })
    },
    async recognizeEditImage(mode = 'overwrite'){
      if(!this.editForm.image){ this.editOcrMsg = '请先选择图片'; return }
      this.submitting = true
      this.editOcrMsg = '正在处理图片…'
      
      let fileToUpload = this.editForm.image
      try {
          fileToUpload = await this.compressImage(fileToUpload)
      } catch(e) {
          console.error('Compression failed, using original', e)
      }

      this.editOcrMsg = '正在识别…'
      const fd = new FormData()
      fd.append('image', fileToUpload)
      fd.append('engine', 'qwen')
      request_zzquant({ url:'/topic/table/ocr', method:'POST', data: fd, headers:{ 'Content-Type':'multipart/form-data' }, timeout: 60000 })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000){
            const rows = d.data || []
            const newRows = Array.isArray(rows) ? rows : []
            if (mode === 'append') {
              this.editForm.rows = [...(this.editForm.rows || []), ...newRows]
              this.editOcrMsg = newRows.length ? '识别成功并已追加内容' : '未识别到表格内容'
            } else {
              this.editForm.rows = newRows
              this.editOcrMsg = newRows.length ? '识别成功并已覆盖内容' : '未识别到表格内容'
            }
          } else {
            const msg = d.data || d.message || '识别失败'
            this.editOcrMsg = typeof msg === 'string' ? msg : '识别失败'
          }
        })
        .catch(err=>{ const msg = (err && err.message) ? String(err.message) : '识别失败'; this.editOcrMsg = msg })
        .finally(()=>{ this.submitting = false })
    },
    async mergeDeepSeek() {
      if (!this.editForm.article_id) return
      
      this.merging = true
      try {
        const res = await request_zzquant({
          url: '/topic/table/merge_content',
          method: 'POST',
          data: {
            article_id: this.editForm.article_id,
            current_content: this.editForm.content
          },
          timeout: 180000
        })
        
        const d = res.data || {}
        if (d.code === 20000 && d.data && d.data.merged_content) {
          this.editForm.content = d.data.merged_content
          this.$bvToast.toast('内容已融合更新', {
            title: 'AI 融合成功',
            variant: 'success',
            solid: true
          })
        } else {
          this.$bvToast.toast((d.data && d.data.message) || d.message || '融合失败', {
            title: '融合提示',
            variant: 'warning',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('请求失败，请稍后重试', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.merging = false
      }
    },
    updateTable(){
      if (this.viewMode === 'json') {
          this.syncJsonToRows();
          if (this.jsonError) {
             alert(this.jsonError);
             return;
          }
      }
      if(!this.canEdit || !this.editId) return
      this.submitting = true
      const payload = { 
        name: this.editForm.name, 
        content: this.editForm.content, 
        source: this.editForm.source,
        article_id: this.editForm.article_id,
        rows: this.editForm.rows,
        created_time: this.editForm.created_time,
        updated_time: this.editForm.updated_time
      }
      request_zzquant({ url:'/topic/table/update/'+this.editId, method:'POST', data: payload })
        .then(res=>{
          const d = res.data || {}
          if(d.code === 20000){
            this.$bvToast.toast('保存成功', { variant: 'success', solid: true })
            this.isEditing = false
          } else {
            const msg = d.data || d.message || '更新失败'
            alert(typeof msg === 'string' ? msg : '更新失败')
          }
        })
        .catch(err=>{ console.error(err); alert('请求失败') })
        .finally(()=>{ this.submitting=false })
    },
    addRow(index) {
      const newRow = { '一级大类': '', '二级小类': '', '三级细分': '', '个股': '', '相关性': '', '信息源': '' };
      this.editForm.rows.splice(index + 1, 0, newRow);
    },
    formatNow(){
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      const y = d.getFullYear()
      const m = pad(d.getMonth()+1)
      const day = pad(d.getDate())
      const hh = pad(d.getHours())
      const mm = pad(d.getMinutes())
      const ss = pad(d.getSeconds())
      return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
    },
    queryStockReturns(){
      if(!this.editForm.rows || !this.editForm.rows.length) return
      const stocks = this.editForm.rows.map(r => r['个股']).filter(s => s && s.trim())
      if(!stocks.length){ return }
      
      this.loadingStocks = true
      this.lastQuoteTime = this.formatNow()
      
      request_zzquant({ url:'/v3/topic/table/stock-returns', method:'POST', data: { stocks } })
      .then(res => {
         const d = res.data
         if(d.code === 20000){
            const result = d.data || {}
            const pickCircCap = (obj) => {
              if (!obj || typeof obj !== 'object') return ''
              const keys = ['circ_cap','circulation_cap','circulating_cap','circ','circ_price','circ_value','circulation_value']
              for (let i=0;i<keys.length;i++){
                const k = keys[i]
                if (obj[k] !== undefined && obj[k] !== null) return obj[k]
              }
              return ''
            }
            this.editForm.rows = this.editForm.rows.map(row => {
               const stock = (row['个股'] || '').trim()
               if(stock && result[stock]){
                  return {
                     ...row,
                     '涨跌幅': result[stock].px_change_rate,
                     '最新价': result[stock].last_px,
                     '流通市值': pickCircCap(result[stock]),
                     '10日涨幅': result[stock].px_change_rate_10d,
                     '20日涨幅': result[stock].px_change_rate_20d,
                     'symbol_code': result[stock].symbol || result[stock].code || result[stock].ts_code || result[stock].symbol_code
                  }
               }
               return row
            })
            if(this.viewMode === 'mindmap'){ this.generateMindMap() }
            if (this.viewMode === 'kline' && this.showStockComparison) {
              this.fetchComparisonKlines()
            }
         }
      })
      .catch(e => { console.error(e) })
      .finally(() => { this.loadingStocks = false })
    },
    onToggleQuotes(val){
      if(val){
        this.queryStockReturns()
      } else {
        if(this.viewMode === 'mindmap'){ this.generateMindMap() }
      }
    },
    switchToTableMode(){
      if(this.viewMode === 'json'){ this.syncJsonToRows() }
      this.viewMode = 'table'
    },
    switchToMindMap(){
      this.viewMode = 'mindmap'
      this.generateMindMap()
    },
    switchToJsonMode(){
      this.jsonStr = JSON.stringify(this.editForm.rows, null, 2)
      this.viewMode = 'json'
    },
    switchToKlineMode() {
      this.viewMode = 'kline'
      if (!this.klineData || this.klineData.length === 0) {
        this.fetchKlineData()
      } else {
        this.generateKlineOption()
        if (this.showStockComparison) {
          this.fetchComparisonKlines()
        }
      }
    },
    fetchKlineData() {
      this.loadingKline = true
      this.requireSubscription = false
      request_zzquant({ url: `/v3/topic/table/${this.editId}/kline`, method: 'GET' })
        .then(res => {
          const d = res.data
          if (d.code === 20000) {
            this.klineData = d.data || []
            if (this.klineData.length > 0 && !this.comparisonBaseDate) {
              this.setDefaultComparisonBaseDate()
            }
            this.generateKlineOption()
            if (this.showStockComparison) {
              this.fetchComparisonKlines()
            }
          }
        })
        .catch(err => {
          const status = err.status || (err.response && err.response.status)
          if (status === 403) {
            this.requireSubscription = true
            this.generateDummyKline()
          }
        })
        .finally(() => {
          this.loadingKline = false
        })
    },
    generateDummyKline() {
      const now = new Date()
      this.klineData = Array.from({length: 30}).map((_, i) => {
        const d = new Date(now.getTime() - (30 - i) * 24 * 3600 * 1000)
        return {
          trade_date: d.toISOString().split('T')[0],
          open: 1000 + Math.random() * 100,
          close: 1000 + Math.random() * 100,
          high: 1100 + Math.random() * 50,
          low: 950 + Math.random() * 50,
          prev_close: 1000,
          amount: Math.random() * 1000000000,
          market_timing: Math.random() > 0.5 ? 1 : -1
        }
      })
      if (this.klineData.length > 0 && !this.comparisonBaseDate) {
        this.setDefaultComparisonBaseDate()
      }
      this.generateKlineOption()
    },
    syncKlineData() {
      this.syncingKline = true
      request_zzquant({ url: `/v3/topic/table/${this.editId}/kline/sync`, method: 'POST' })
        .then(() => {
          this.$bvToast.toast('同步任务已启动，请稍后刷新', { variant: 'info', solid: true })
          setTimeout(() => this.fetchKlineData(), 3000)
        })
        .finally(() => {
          this.syncingKline = false
        })
    },
    onToggleIndicator(fieldName) {
      if (fieldName === 'showIntervalGain' && this.showIntervalGain) {
        this.showUpLimit = false
        this.showDownLimit = false
        this.showMorningLeader = false
      } else if (fieldName !== 'showIntervalGain' && (this.showUpLimit || this.showDownLimit || this.showMorningLeader)) {
        this.showIntervalGain = false
      }
      this.generateKlineOption()
      if (this.showStockComparison) {
        this.fetchComparisonKlines()
      }
    },
    onToggleStockComparison(val) {
      if (val) {
        this.fetchComparisonKlines()
      } else {
        this.generateKlineOption()
      }
    },
    fetchComparisonKlines() {
      if (!this.klineData || this.klineData.length === 0) return
      
      const map = this.stockNameCodeMap || {}
      const targetCount = this.comparisonStockLimit || 10
      
      const ensureSuffix = (code) => {
        if (!code) return code
        if (code.includes('.')) return code
        if (code.startsWith('60') || code.startsWith('68')) return code + '.SH'
        if (code.startsWith('00') || code.startsWith('30') || code.startsWith('002')) return code + '.SZ'
        return code
      }

      const start = this.klineData[0].trade_date.replace(/-/g, '')
      const end = this.klineData[this.klineData.length - 1].trade_date.replace(/-/g, '')

      if (this.showIntervalGain) {
        const allCodes = Object.values(map).map(c => ensureSuffix(c)).filter(c => c && c.length >= 6)
        if (allCodes.length === 0) {
          this.comparisonKlineData = {}
          return
        }
        
        // Populate stock names map for display convenience
        Object.keys(map).forEach(name => {
          const code = ensureSuffix(map[name])
          if (code) {
            this.comparisonStockNames[code] = name
          }
        })

        request_zzquant({ 
          url: '/v3/market/kline/day/batch', 
          method: 'POST', 
          data: { codes: allCodes },
          params: { start_date: start, end_date: end, candle_mode: 1 }
        })
        .then(res => {
          if (res.data && res.data.code === 200) {
            this.comparisonKlineData = res.data.data || {}
            this.generateKlineOption()
          }
        })
        .catch(e => console.error('Fetch interval gain klines failed', e))
        return
      }

      const codes = new Set()
      
      // 动态查找符合条件的股票，最多回溯较多天数，尽量凑够叠加只数
      const maxDays = this.comparisonStockLimit === 999 ? this.klineData.length : Math.max(10, Math.ceil(targetCount * 1.5))
      let processedDays = 0

      // 从基准日期往当前日期获取
      const baseDateStr = this.comparisonBaseDate || (this.klineData.length > 0 ? this.klineData[0].trade_date : '')
      let startIdx = this.klineData.findIndex(d => d.trade_date === baseDateStr)
      if (startIdx === -1) startIdx = 0

      for (let i = startIdx; i < this.klineData.length && processedDays < maxDays; i++) {
        const d = this.klineData[i]
        let hasDataThisDay = false

        const extract = (jsonStr) => {
          if (!jsonStr) return
          let stocks = []
          try {
            stocks = JSON.parse(jsonStr)
            if (!Array.isArray(stocks)) stocks = []
          } catch(e) {
            stocks = jsonStr.split(',').map(s => ({ name: s.trim() }))
          }
          
          if (stocks.length > 0) hasDataThisDay = true
          
          stocks.forEach(s => {
            let code = s.code || map[s.name]
            if (code) {
              const fullCode = ensureSuffix(code)
              codes.add(fullCode)
              if (s.name) this.comparisonStockNames[fullCode] = s.name
            }
          })
        }

        // 根据开关动态提取
        if (this.showUpLimit) extract(d.up_limit_stocks)
        if (this.showDownLimit) extract(d.down_limit_stocks)
        if (this.showMorningLeader) extract(d.gt5_top_stocks)
        
        if (hasDataThisDay) processedDays++
        if (codes.size >= targetCount) break
      }
      
      const finalCodes = Array.from(codes).filter(c => c && c.length >= 6)
      console.log(`[Comparison] Found ${finalCodes.length} stocks across ${processedDays} active days.`)

      if (finalCodes.length === 0) {
        this.comparisonKlineData = {}
        return
      }
      
      request_zzquant({ 
        url: '/v3/market/kline/day/batch', 
        method: 'POST', 
        data: { codes: finalCodes },
        params: { start_date: start, end_date: end, candle_mode: 1 }
      })
      .then(res => {
        if (res.data && res.data.code === 200) {
          this.comparisonKlineData = res.data.data || {}
          this.generateKlineOption()
        }
      })
      .catch(e => console.error('Fetch comparison klines failed', e))
    },
    generateKlineOption() {
      if (!this.klineData || this.klineData.length === 0) return
      
      const dates = this.klineData.map(d => d.trade_date)
      const values = this.klineData.map(d => [d.open, d.close, d.high, d.low, d.prev_close])
      const volumes = this.klineData.map(d => d.amount)
      
      const upColor = this.theme === 'dark' ? '#ef4444' : '#ec0000'
      const downColor = this.theme === 'dark' ? '#10b981' : '#00da3c'
      const textColor = this.theme === 'dark' ? '#94a3b8' : '#64748b'
      
      this.klineOption = {
        animation: false,
        tooltip: {
          show: this.showComparisonTooltip,
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#333',
          textStyle: { color: '#eee', fontSize: 12 },
          formatter: (params) => {
            let res = params[0].name + '<br/>'
            let indexHtml = ''
            let timingHtml = ''
            let volumeHtml = ''
            const stockItems = []

            params.forEach(p => {
              if (p.seriesName === '题材K') {
                const d = p.value
                const close = d[2]
                const pre_close = d[5]
                const change = ((close - pre_close) / pre_close * 100).toFixed(2)
                indexHtml = `${p.marker} 指数: <span style="color:${close >= d[1] ? upColor : downColor}">${close} (${change}%)</span><br/>`
                indexHtml += `开盘: ${d[1]} 最高: ${d[3]} 最低: ${d[4]}<br/>`
              } else if (p.seriesName === '择时') {
                const val = p.value
                const text = val > 0 ? '看多' : (val < 0 ? '看空' : '中性')
                const color = val > 0 ? upColor : (val < 0 ? downColor : '#999')
                timingHtml = `${p.marker} 择时: <span style="color:${color}">${text}</span><br/>`
              } else if (p.seriesName === '均成交额') {
                let html = `${p.marker} 均成交额: ${(p.value / 100000000).toFixed(2)} 亿<br/>`
                const item = this.klineData[p.dataIndex]
                if (item) {
                  const formatStocks = (jsonStr, color, label) => {
                    if (!jsonStr) return ''
                    let stocks = []
                    try {
                      stocks = JSON.parse(jsonStr)
                      if (Array.isArray(stocks)) {
                        stocks.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                      }
                    } catch(e) {
                      // ignore
                      stocks = jsonStr.split(',').map(s => ({ name: s }))
                    }
                    if (!stocks.length) return ''
                    const listStr = stocks.map(s => s.time ? `[${s.time}]${s.name}` : s.name).join(' ')
                    return `<span style="color:${color};font-size:11px">${label}: ${listStr}</span><br/>`
                  }
                  if (this.showUpLimit) html += formatStocks(item.up_limit_stocks, upColor, '涨停')
                  if (this.showDownLimit) html += formatStocks(item.down_limit_stocks, downColor, '跌停')
                  if (this.showMorningLeader) html += formatStocks(item.gt5_top_stocks, '#eab308', '早盘领涨')
                }
                volumeHtml = html
              } else if (!this.showIntervalGain && p.seriesName && p.seriesName !== '题材指数(基准)') {
                // 个股性能
                const val = p.value
                if (Array.isArray(val) && val[2] !== undefined) {
                  const change = parseFloat(val[2]) // Close is index 2 in category-axis candlestick data [date, O, C, L, H]
                  const textColor = change >= 0 ? (this.theme === 'dark' ? '#ef4444' : '#ec0000') : (this.theme === 'dark' ? '#10b981' : '#00da3c')
                  stockItems.push({
                    name: p.seriesName,
                    color: p.color,
                    change: isNaN(change) ? -9999 : change,
                    html: `${p.marker} <span style="color:${textColor};font-size:11px">${p.seriesName}: ${val[2]}%</span><br/>`
                  })
                }
              }
            })

            // If showIntervalGain is active, we collect the stock changes from ALL comparison stock data for the hovered date
            if (this.showIntervalGain && this.comparisonKlineData) {
              const hoveredDate = params[0].name
              const dateKey = hoveredDate.replace(/-/g, '')
              const hoveredGains = []
              
              Object.keys(this.comparisonKlineData).forEach(code => {
                const stockKlines = this.comparisonKlineData[code] || []
                const kMap = {}
                stockKlines.forEach(k => { kMap[k.trade_date.replace(/-/g, '')] = k })
                
                let baseClose = null
                const baseDateStr = (this.comparisonBaseDate || dates[0]).replace(/-/g, '')
                if (kMap[baseDateStr]) {
                  baseClose = kMap[baseDateStr].prev_close
                } else {
                  for (let i = dates.length - 1; i >= 0; i--) {
                    const dStr = dates[i].replace(/-/g, '')
                    if (dStr < baseDateStr && kMap[dStr]) {
                      baseClose = kMap[dStr].close
                      break
                    }
                  }
                  if (!baseClose) {
                    for (let i = 0; i < dates.length; i++) {
                      const dStr = dates[i].replace(/-/g, '')
                      if (dStr > baseDateStr && kMap[dStr]) {
                        baseClose = kMap[dStr].prev_close
                        break
                      }
                    }
                  }
                }
                
                if (baseClose && kMap[dateKey]) {
                  const closeVal = kMap[dateKey].close
                  const gain = (closeVal / baseClose - 1) * 100
                  const findName = (c) => {
                    if (this.comparisonStockNames[c]) return this.comparisonStockNames[c]
                    const pure = c.split('.')[0]
                    const row = this.editForm.rows.find(r => {
                      const rc = r.symbol_code || r['股票代码'] || ''
                      return rc === c || rc === pure
                    })
                    return (row || {}).个股 || c
                  }
                  const name = findName(code)
                  hoveredGains.push({ name, gain, code })
                }
              })
              
              hoveredGains.sort((a, b) => b.gain - a.gain)
              
              const limit = this.comparisonStockLimit || 10
              const topHovered = hoveredGains.slice(0, limit)
              
              topHovered.forEach(item => {
                const seriesColor = this.comparisonStockColors ? this.comparisonStockColors[item.code] : null
                const color = seriesColor || (item.gain >= 0
                  ? (this.theme === 'dark' ? '#ef4444' : '#ec0000')
                  : (this.theme === 'dark' ? '#10b981' : '#00da3c'))
                
                const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`
                const textColor = item.gain >= 0 ? (this.theme === 'dark' ? '#ef4444' : '#ec0000') : (this.theme === 'dark' ? '#10b981' : '#00da3c')
                stockItems.push({
                  name: item.name,
                  change: item.gain,
                  html: `${marker} <span style="color:${textColor};font-size:11px">${item.name}: ${item.gain >= 0 ? '+' : ''}${item.gain.toFixed(2)}%</span><br/>`
                })
              })
            }

            // 按涨幅从高到低排序个股
            stockItems.sort((a, b) => b.change - a.change)

            // 先输出个股列表
            stockItems.forEach(item => {
              res += item.html
            })

            // 输出汇总数据
            if (indexHtml) res += indexHtml
            if (volumeHtml) res += volumeHtml
            if (timingHtml) res += timingHtml

            return res
          }
        },
        grid: [
          { left: '50', right: '30', top: '8%', height: this.showStockComparison ? '25%' : '62%' },
          { left: '50', right: '30', top: this.showStockComparison ? '35%' : '75%', height: '8%' },
          { left: '50', right: '30', top: '2%', height: '4%' },
          { left: '50', right: '30', top: '50%', height: this.showStockComparison ? '40%' : '0%', show: this.showStockComparison }
        ],
        legend: {
          show: this.showStockComparison,
          top: '45%',
          left: 'center',
          textStyle: { color: textColor, fontSize: 10 },
          itemWidth: 10,
          itemHeight: 10
        },
        xAxis: [
          { 
            type: 'category', 
            data: dates, 
            boundaryGap: true, 
            axisLine: { onZero: false, lineStyle: { color: textColor } }, 
            splitLine: { show: false },
            axisLabel: { color: textColor, fontSize: 10 }
          },
          { 
            type: 'category', 
            gridIndex: 1, 
            data: dates, 
            boundaryGap: true, 
            axisLine: { onZero: false, lineStyle: { color: textColor } }, 
            axisTick: { show: false }, 
            splitLine: { show: false }, 
            axisLabel: { show: false }
          },
          { 
            type: 'category', 
            gridIndex: 2, 
            data: dates, 
            boundaryGap: true, 
            axisLine: { show: false }, 
            axisTick: { show: false }, 
            splitLine: { show: false }, 
            axisLabel: { show: false }
          },
          { 
            type: 'category', 
            gridIndex: 3, 
            data: dates, 
            boundaryGap: true, 
            axisLine: { onZero: false, lineStyle: { color: textColor } }, 
            splitLine: { show: false, lineStyle: { type: 'dashed', opacity: 0.1 } },
            axisLabel: { color: textColor, fontSize: 10 },
            show: this.showStockComparison
          }
        ],
        yAxis: [
          { 
            scale: true, 
            splitLine: { show: true, lineStyle: { type: 'dashed', opacity: 0.1 } },
            axisLabel: { color: textColor, fontSize: 10 },
            axisLine: { lineStyle: { color: textColor } }
          },
          { 
            type: 'value',
            gridIndex: 1, 
            splitNumber: 2, 
            axisLabel: { show: false }, 
            axisLine: { show: false }, 
            axisTick: { show: false }, 
            splitLine: { show: false } 
          },
          { 
            type: 'value',
            gridIndex: 2, 
            min: -1,
            max: 1,
            splitNumber: 1,
            axisLabel: { show: false }, 
            axisLine: { show: false }, 
            axisTick: { show: false }, 
            splitLine: { show: false } 
          },
          {
            type: 'value',
            gridIndex: 3,
            scale: true,
            axisLabel: { 
              color: textColor, 
              fontSize: 10,
              formatter: '{value}%'
            },
            splitLine: { show: true, lineStyle: { type: 'dashed', opacity: 0.1 } },
            show: this.showStockComparison
          }
        ],
        axisPointer: {
          link: { xAxisIndex: 'all' }
        },
        dataZoom: [
          { 
            type: 'inside', 
            xAxisIndex: [0, 1, 2, 3], 
            start: this.klineData.length > 60 ? 100 - (60 / this.klineData.length * 100) : 0, 
            end: 100 
          },
          { 
            show: true, 
            xAxisIndex: [0, 1, 2, 3], 
            type: 'slider', 
            top: '90%', 
            height: '15',
            start: this.klineData.length > 60 ? 100 - (60 / this.klineData.length * 100) : 0, 
            end: 100,
            textStyle: { color: textColor }
          }
        ],
        series: [
          {
            name: '题材K',
            type: 'candlestick',
            data: values,
            itemStyle: {
              color: upColor,
              color0: downColor,
              borderColor: upColor,
              borderColor0: downColor
            },
            markPoint: {
              symbol: 'rect',
              symbolSize: [60, 16],
              itemStyle: {
                color: 'transparent',
                borderColor: 'transparent',
                cursor: 'pointer'
              },
              label: {
                show: true,
                position: 'inside',
                formatter: (params) => {
                  return params.data.value
                },
                fontSize: 10,
                color: upColor,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                padding: [2, 4],
                borderRadius: 2
              },
              data: (() => {
                if (this.klineLabelMode === 'none') return []
                const pts = []
                
                const upLabelColor = this.theme === 'dark' ? '#f87171' : '#dc2626'
                const downLabelColor = this.theme === 'dark' ? '#4ade80' : '#16a34a'
                const leaderLabelColor = this.theme === 'dark' ? '#fbbf24' : '#d97706'
                const labelBg = this.theme === 'dark' ? 'rgba(30, 41, 59, 0.85)' : 'rgba(255, 255, 255, 0.95)'
                const labelBorder = this.theme === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.8)'

                // Pre-compute baseClose for all compared stocks to avoid redundant loops
                const stockBaseCloseMap = {}
                const baseDateStr = (this.comparisonBaseDate || dates[0]).replace(/-/g, '')
                if (this.showIntervalGain && this.comparisonKlineData) {
                  Object.keys(this.comparisonKlineData).forEach(code => {
                    const stockKlines = this.comparisonKlineData[code] || []
                    const kMap = {}
                    stockKlines.forEach(k => { kMap[k.trade_date.replace(/-/g, '')] = k })
                    
                    let baseClose = null
                    if (kMap[baseDateStr]) {
                      baseClose = kMap[baseDateStr].prev_close
                    } else {
                      for (let i = dates.length - 1; i >= 0; i--) {
                        const dStr = dates[i].replace(/-/g, '')
                        if (dStr < baseDateStr && kMap[dStr]) {
                          baseClose = kMap[dStr].close
                          break
                        }
                      }
                      if (!baseClose) {
                        for (let i = 0; i < dates.length; i++) {
                          const dStr = dates[i].replace(/-/g, '')
                          if (dStr > baseDateStr && kMap[dStr]) {
                            baseClose = kMap[dStr].prev_close
                            break
                          }
                        }
                      }
                    }
                    if (baseClose) {
                      stockBaseCloseMap[code] = { baseClose, kMap }
                    }
                  })
                }

                this.klineData.forEach(d => {
                  let upStocks = []
                  let downStocks = []
                  let gt5Stocks = []
                  
                  if (this.showUpLimit && d.up_limit_stocks) {
                    try {
                      upStocks = JSON.parse(d.up_limit_stocks)
                    } catch (e) {
                      upStocks = d.up_limit_stocks.split(',').map(s => ({ name: s, time: '' }))
                    }
                    upStocks.sort((a, b) => (b.time || '').localeCompare(a.time || ''))
                  }

                  if (this.showDownLimit && d.down_limit_stocks) {
                    try {
                      downStocks = JSON.parse(d.down_limit_stocks)
                    } catch (e) {
                      downStocks = d.down_limit_stocks.split(',').map(s => ({ name: s, time: '' }))
                    }
                    downStocks.sort((a, b) => (b.time || '').localeCompare(a.time || ''))
                  }
                  
                  if (this.showMorningLeader && d.gt5_top_stocks) {
                    try {
                      gt5Stocks = JSON.parse(d.gt5_top_stocks)
                    } catch (e) {
                      gt5Stocks = d.gt5_top_stocks.split(',').map(s => ({ name: s, time: '' }))
                    }
                    gt5Stocks.sort((a, b) => (b.time || '').localeCompare(a.time || ''))
                  }
                  
                  let allStocks = []
                  downStocks.forEach(s => allStocks.push({ s, type: 'down', color: downLabelColor }))
                  gt5Stocks.forEach(s => allStocks.push({ s, type: 'leader', color: leaderLabelColor }))
                  upStocks.forEach(s => allStocks.push({ s, type: 'up', color: upLabelColor }))

                  // Add top 3 interval gain stocks daily if showIntervalGain is enabled and date is >= base date
                  if (this.showIntervalGain && this.comparisonBaseDate && d.trade_date >= this.comparisonBaseDate) {
                    const gainStocks = []
                    Object.keys(stockBaseCloseMap).forEach(code => {
                      const { baseClose, kMap } = stockBaseCloseMap[code]
                      const dateKey = d.trade_date.replace(/-/g, '')
                      if (kMap[dateKey]) {
                        const closeVal = kMap[dateKey].close
                        const gain = (closeVal / baseClose - 1) * 100
                        
                        const findName = (c) => {
                          if (this.comparisonStockNames[c]) return this.comparisonStockNames[c]
                          const pure = c.split('.')[0]
                          const row = this.editForm.rows.find(r => {
                            const rc = r.symbol_code || r['股票代码'] || ''
                            return rc === c || rc === pure
                          })
                          return (row || {}).个股 || c
                        }
                        const name = findName(code)
                        gainStocks.push({ name, gain })
                      }
                    })
                    
                    gainStocks.sort((a, b) => b.gain - a.gain)
                    const top10 = gainStocks.slice(0, 10)
                    top10.forEach(s => {
                      const text = `${s.name} ${s.gain >= 0 ? '+' : ''}${s.gain.toFixed(2)}%`
                      const color = s.gain >= 0 ? (this.theme === 'dark' ? '#f87171' : '#dc2626') : (this.theme === 'dark' ? '#4ade80' : '#16a34a')
                      allStocks.push({
                        s: { name: s.name },
                        type: 'interval_gain',
                        color: color,
                        labelText: text
                      })
                    })
                  }

                  if (allStocks.length > this.klineLabelLimit) {
                    allStocks = allStocks.slice(0, this.klineLabelLimit)
                  }

                  let idx = 0
                  allStocks.forEach(item => {
                    const s = item.s
                    const color = item.color
                    const labelText = item.labelText !== undefined ? item.labelText : (this.klineLabelMode === 'all'
                      ? (s.time ? `[${s.time}] ${s.name}` : s.name)
                      : s.name)
                      
                    pts.push({
                      name: s.name,
                      value: labelText,
                      coord: [d.trade_date, this.stockLabelPosition === 'above' ? d.high : d.low],
                      symbolOffset: [0, this.stockLabelPosition === 'above' ? (-idx * 16 - 5) : (idx * 16 + 5)],
                      label: { 
                        show: true, 
                        color: color,
                        fontSize: 9,
                        backgroundColor: labelBg,
                        borderColor: labelBorder,
                        borderWidth: 1,
                        padding: [1, 3],
                        borderRadius: 2
                      }
                    })
                    idx++
                  })
                })
                return pts
              })()
            },
            markLine: this.showStockComparison ? {
              symbol: ['none', 'none'],
              silent: true,
              label: {
                show: true,
                position: 'end',
                formatter: '基准日',
                fontSize: 9,
                color: this.theme === 'dark' ? '#93c5fd' : '#1e3a8a',
                backgroundColor: this.theme === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(239, 246, 255, 0.8)',
                padding: [1, 3],
                borderRadius: 2
              },
              lineStyle: {
                color: '#3b82f6',
                type: 'dashed',
                width: 1,
                opacity: 0.6
              },
              data: [
                { xAxis: this.comparisonBaseDate || dates[0] }
              ]
            } : undefined
          },
          {
            name: '均成交额',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: volumes,
            itemStyle: {
              color: (params) => {
                const i = params.dataIndex
                return values[i][1] >= values[i][0] ? upColor : downColor
              }
            }
          },
          {
            name: '择时',
            type: 'bar',
            xAxisIndex: 2,
            yAxisIndex: 2,
            barMaxWidth: 20,
            data: this.klineData.map(d => d.market_timing || 0),
            itemStyle: {
              color: (params) => {
                const val = params.value
                return val > 0 ? upColor : (val < 0 ? downColor : 'transparent')
              },
              borderRadius: [2, 2, 2, 2]
            },
            label: {
              show: true,
              position: 'inside',
              formatter: (params) => {
                const val = params.value
                if (val > 0) return '多'
                if (val < 0) return '空'
                return ''
              },
              color: '#fff',
              fontSize: 10,
              fontWeight: 'bold'
            }
          },
          // 题材指数归一化叠加 (K线形式)
          ...(this.showStockComparison ? [{
            name: '题材指数(基准)',
            type: 'candlestick',
            xAxisIndex: 3,
            yAxisIndex: 3,
            itemStyle: {
              color: 'rgba(150, 150, 150, 0.5)',
              color0: 'rgba(100, 100, 100, 0.5)',
              borderColor: 'rgba(150, 150, 150, 0.8)',
              borderColor0: 'rgba(100, 100, 100, 0.8)',
              opacity: 0.6
            },
            data: (() => {
              let base = null
              const baseDateStr = (this.comparisonBaseDate || dates[0]).replace(/-/g, '')
              const baseIdx = dates.findIndex(d => d.replace(/-/g, '') === baseDateStr)
              if (baseIdx !== -1 && values[baseIdx]) {
                base = values[baseIdx][4] // base date's prev_close (昨收)
              }
              if (!base) base = values[0][4] // fallback
              return values.map(v => {
                return [
                  ((v[0] / base - 1) * 100).toFixed(2),
                  ((v[1] / base - 1) * 100).toFixed(2),
                  ((v[3] / base - 1) * 100).toFixed(2),
                  ((v[2] / base - 1) * 100).toFixed(2)
                ]
              })
            })(),
            markLine: {
              symbol: ['none', 'none'],
              silent: true,
              label: {
                show: true,
                position: 'end',
                formatter: '基准起点',
                fontSize: 9,
                color: this.theme === 'dark' ? '#93c5fd' : '#1e3a8a',
                backgroundColor: this.theme === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(239, 246, 255, 0.8)',
                padding: [1, 3],
                borderRadius: 2
              },
              lineStyle: {
                color: '#3b82f6',
                type: 'dashed',
                width: 1,
                opacity: 0.6
              },
              data: [
                { xAxis: this.comparisonBaseDate || dates[0] }
              ]
            }
          }] : []),
          // 个股归一化叠加 (K线形式)
          ...(this.showStockComparison ? (() => {
            const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc']
            
            // If showIntervalGain is active, only draw series for the top targetCount stocks by final gain
            let codesToRender = Object.keys(this.comparisonKlineData)
            if (this.showIntervalGain) {
              const rawData = this.comparisonKlineData
              const baseDateStr = (this.comparisonBaseDate || dates[0]).replace(/-/g, '')
              const gains = []
              
              Object.keys(rawData).forEach(code => {
                const klines = rawData[code] || []
                const kMap = {}
                klines.forEach(k => { kMap[k.trade_date.replace(/-/g, '')] = k })
                
                let baseClose = null
                if (kMap[baseDateStr]) {
                  baseClose = kMap[baseDateStr].prev_close
                } else {
                  for (let i = dates.length - 1; i >= 0; i--) {
                    const dStr = dates[i].replace(/-/g, '')
                    if (dStr < baseDateStr && kMap[dStr]) {
                      baseClose = kMap[dStr].close
                      break
                    }
                  }
                  if (!baseClose) {
                    for (let i = 0; i < dates.length; i++) {
                      const dStr = dates[i].replace(/-/g, '')
                      if (dStr > baseDateStr && kMap[dStr]) {
                        baseClose = kMap[dStr].prev_close
                        break
                      }
                    }
                  }
                }
                
                let lastClose = null
                for (let i = dates.length - 1; i >= 0; i--) {
                  const dStr = dates[i].replace(/-/g, '')
                  if (kMap[dStr]) {
                    lastClose = kMap[dStr].close
                    break
                  }
                }
                
                if (baseClose && lastClose) {
                  const gain = (lastClose / baseClose - 1) * 100
                  gains.push({ code, gain })
                }
              })
              
              gains.sort((a, b) => b.gain - a.gain)
              const targetCount = this.comparisonStockLimit || 10
              codesToRender = gains.slice(0, targetCount).map(x => x.code)
            }
            
            // Build stock to color map for tooltip usage
            this.comparisonStockColors = {}
            codesToRender.forEach((code, idx) => {
              this.comparisonStockColors[code] = colors[idx % colors.length]
            })

            return codesToRender.map((code, idx) => {
              const stockKlines = this.comparisonKlineData[code] || []
              const kMap = {}
              stockKlines.forEach(k => { kMap[k.trade_date.replace(/-/g, '')] = k })
              
              const findName = (c) => {
                if (this.comparisonStockNames[c]) return this.comparisonStockNames[c]
                const pure = c.split('.')[0]
                const row = this.editForm.rows.find(r => {
                  const rc = r.symbol_code || r['股票代码'] || ''
                  return rc === c || rc === pure
                })
                return (row || {}).个股 || c
              }
              const stockName = findName(code)
              const color = this.comparisonStockColors[code] || colors[idx % colors.length]
              
              let base = null
              const baseDateStr = (this.comparisonBaseDate || dates[0]).replace(/-/g, '')
              if (kMap[baseDateStr]) {
                base = kMap[baseDateStr].prev_close
              } else {
                // Scan backward for last trade date before base date
                for (let i = dates.length - 1; i >= 0; i--) {
                  const dateStr = dates[i].replace(/-/g, '')
                  if (dateStr < baseDateStr && kMap[dateStr]) {
                    base = kMap[dateStr].close
                    break
                  }
                }
                if (!base) {
                  // Scan forward for first trade date after base date
                  for (let i = 0; i < dates.length; i++) {
                    const dateStr = dates[i].replace(/-/g, '')
                    if (dateStr > baseDateStr && kMap[dateStr]) {
                      base = kMap[dateStr].prev_close
                      break
                    }
                  }
                }
              }
              
              const seriesData = dates.map(d => {
                const dateStr = d.replace(/-/g, '')
                const k = kMap[dateStr]
                if (!k || !base) return [null, null, null, null]
                return [
                  ((k.open / base - 1) * 100).toFixed(2),
                  ((k.close / base - 1) * 100).toFixed(2),
                  ((k.low / base - 1) * 100).toFixed(2),
                  ((k.high / base - 1) * 100).toFixed(2)
                ]
              })
              
              return {
                name: stockName,
                type: 'candlestick',
                xAxisIndex: 3,
                yAxisIndex: 3,
                barMaxWidth: 6,
                itemStyle: {
                  color: color,
                  color0: color,
                  borderColor: color,
                  borderColor0: color,
                  opacity: 0.4
                },
                data: seriesData
              }
            })
          })() : [])
        ]
      }
    },
    syncJsonToRows(){
       try {
          const parsed = JSON.parse(this.jsonStr)
          if (Array.isArray(parsed)) {
            this.editForm.rows = parsed
            this.jsonError = ''
          } else {
            this.jsonError = 'JSON必须是数组格式'
          }
        } catch (e) {
          this.jsonError = 'JSON格式错误: ' + e.message
        }
    },
    enterMindMapFullscreen(){
      const el = this.$refs.mindmapContainer
      if (!el) return
      try {
        if (el.requestFullscreen) el.requestFullscreen()
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen()
        else if (el.msRequestFullscreen) el.msRequestFullscreen()
      } catch(e) { console.warn('Fullscreen failed', e) }
    },
    downloadMindMap(){
      const chartRef = this.$refs.mindmapChart
      if (!chartRef) { alert('未找到图表'); return }
      let inst = null
      try {
        if (typeof chartRef.getEchartsInstance === 'function') { inst = chartRef.getEchartsInstance() } 
        else if (chartRef.chart) { inst = chartRef.chart } 
        else if (chartRef.echarts) { inst = chartRef.echarts }
      } catch(e) { console.warn('获取echarts实例失败', e) }
      if (!inst || typeof inst.getDataURL !== 'function') { alert('无法获取图像'); return }
      const dataURL = inst.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' })
      const a = document.createElement('a')
      const name = (this.editForm.name || '思维导图')
      const ts = this.lastQuoteTime || this.editForm.updated_time || ''
      a.href = dataURL
      a.download = `${name}-${ts || 'snapshot'}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    changeMindMapStyle(style){ this.mindMapStyle = style; this.generateMindMap() },
    changeMindMapCurve(curve){ this.mindMapCurve = curve; this.generateMindMap() },
    changeMindMapSymbol(symbol){ this.mindMapSymbol = symbol; this.generateMindMap() },
    generateMindMap(){
      const data = { name: this.editForm.name || '题材', children: [] }
      const map = {}
      const layoutMap = {
          'LR': { orient: 'LR', top: '5%', left: '5%', bottom: '5%', right: '70%', labelPos: 'left', leafLabelPos: 'right', labelAlign: 'right', leafLabelAlign: 'left' },
          'RL': { orient: 'RL', top: '5%', left: '70%', bottom: '5%', right: '5%', labelPos: 'right', leafLabelPos: 'left', labelAlign: 'left', leafLabelAlign: 'right' },
          'TB': { orient: 'TB', top: '5%', left: '5%', bottom: '70%', right: '5%', labelPos: 'top', leafLabelPos: 'bottom', labelAlign: 'center', leafLabelAlign: 'center', labelVertical: 'bottom', leafLabelVertical: 'top' },
          'BT': { orient: 'BT', top: '70%', left: '5%', bottom: '5%', right: '5%', labelPos: 'bottom', leafLabelPos: 'top', labelAlign: 'center', leafLabelAlign: 'center', labelVertical: 'top', leafLabelVertical: 'bottom' }
      }
      const currentStyle = layoutMap[this.mindMapStyle] || layoutMap['LR']
      const stockLabel = {
          position: currentStyle.leafLabelPos,
          verticalAlign: currentStyle.leafLabelVertical || 'middle',
          align: currentStyle.leafLabelAlign,
          fontSize: 12,
          rotate: (this.mindMapStyle === 'TB' || this.mindMapStyle === 'BT') ? 90 : 0
      }

      ;(this.editForm.rows || []).forEach(row => {
          const l1 = (row['一级大类'] || '其他').trim()
          const l2 = (row['二级小类'] || '').trim()
          const l3 = (row['三级细分'] || '').trim()
          const stock = (row['个股'] || '').trim()
          if (!stock) return

          let stockName = stock
          let nodeColor = undefined
          let nodeFontWeight = undefined

          if (this.showQuotes && row['涨跌幅'] !== undefined && row['涨跌幅'] !== null) {
             stockName += ` (${row['涨跌幅']}%)`
             const rate = parseFloat(row['涨跌幅'])
             if (!isNaN(rate)) {
                if (rate > 9) { nodeColor = 'red'; nodeFontWeight = 'bold' } 
                else if (rate > 0) { nodeColor = 'red' } 
                else if (rate < 0) { nodeColor = 'green' }
             }
          }

          if (!map[l1]) map[l1] = {}
          if (!map[l1][l2]) map[l1][l2] = {}
          if (!map[l1][l2][l3]) map[l1][l2][l3] = []

          map[l1][l2][l3].push({ 
             name: stockName, 
             value: row['相关性'] || '', 
             source: row['信息源'] || '',
             color: nodeColor,
             fontWeight: nodeFontWeight
          })
      })

      const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc']
      let colorIndex = 0

      for (const l1 in map) {
          const color = colors[colorIndex % colors.length]
          colorIndex++
          const l1Node = { name: l1, children: [], itemStyle: { color, borderColor: color }, lineStyle: { color } }
          for (const l2 in map[l1]) {
                  let l2Parent = l1Node
                  if (l2) {
                    const l2Node = { name: l2, children: [], itemStyle: { color, borderColor: color }, lineStyle: { color } }
                    l1Node.children.push(l2Node)
                    l2Parent = l2Node
                  }

                  for (const l3 in map[l1][l2]) {
                      let l3Parent = l2Parent
                      if (l3) {
                          const l3Node = { name: l3, children: [], itemStyle: { color, borderColor: color }, lineStyle: { color } }
                          l2Parent.children.push(l3Node)
                          l3Parent = l3Node
                      }

                      map[l1][l2][l3].forEach(s => {
                          const stockNode = {
                              name: s.name,
                              children: [],
                              itemStyle: { color, borderColor: color },
                              lineStyle: { color },
                              label: { ...stockLabel, color: s.color, fontWeight: s.fontWeight }
                          }
                          const truncate = (str, len=200) => str.length > len ? str.substring(0, len) + '...' : str
                          let lastNode = stockNode
        
                          if (this.canEdit && s.source) {
                            const sourceNode = { 
                              name: truncate(s.source) + '：' + truncate(s.value),
                              value: s.value, 
                              itemStyle: { color, borderColor: color },
                              lineStyle: { color },
                              children: []
                            }
                            lastNode.children.push(sourceNode)
                          } else if (s.value) {
                            const relNode = { 
                              name: truncate(s.value),
                              value: s.value, 
                              itemStyle: { color, borderColor: color },
                              lineStyle: { color },
                              children: []
                            }
                            lastNode.children.push(relNode)
                          }
                          l3Parent.children.push(stockNode)
                      })
                  }
              }
          data.children.push(l1Node)
      }

      const getLayoutOption = (style) => {
          const base = {
              type: 'tree', data: [data],
              symbol: this.mindMapSymbol, edgeShape: this.mindMapCurve, symbolSize: 7,
              roam: true, expandAndCollapse: true, initialTreeDepth: -1,
              animationDuration: 550, animationDurationUpdate: 750
          }
          const opt = layoutMap[style] || layoutMap['LR']
          return {
              ...base,
              layout: 'orthogonal', orient: opt.orient,
              top: opt.top, left: opt.left, bottom: opt.bottom, right: opt.right,
              label: { 
                  position: opt.labelPos, verticalAlign: opt.labelVertical || 'middle', align: opt.labelAlign, 
                  fontSize: 12, rotate: (style === 'TB' || style === 'BT') ? 90 : 0
              },
              leaves: { 
                  label: { 
                      position: opt.leafLabelPos, verticalAlign: opt.leafLabelVertical || 'middle', align: opt.leafLabelAlign,
                      rotate: (style === 'TB' || style === 'BT') ? 90 : 0
                  } 
              }
          }
      }

      this.mindMapOption = {
          title: [
            { text: this.editForm.updated_time ? '题材更新时间：' + this.editForm.updated_time : '', top: 10, left: 10, textStyle: { fontSize: 12, color: '#999', fontWeight: 'normal' } },
            { text: this.lastQuoteTime ? '行情更新时间：' + this.lastQuoteTime : '', top: 10, right: 10, textStyle: { fontSize: 12, color: '#999', fontWeight: 'normal' } }
          ],
          backgroundColor: this.theme === 'dark' ? '#1c1c1e' : '#ffffff',
          tooltip: { trigger: 'item', triggerOn: 'mousemove', formatter: (params) => params.data.value ? params.data.value : params.name },
          series: [getLayoutOption(this.mindMapStyle)],
          graphic: [
            { type: 'text', z: -10, right: 20, bottom: 60, style: { fill: '#e0e0e0', text: '自在量化', font: 'bold 50px sans-serif' } },
            { type: 'text', z: -10, right: 20, bottom: 20, style: { fill: '#e0e0e0', text: 'quant.zizizaizai.com', font: '24px sans-serif' } },
            { type: 'text', z: -10, left: 'center', bottom: 10, style: { fill: '#999999', text: '声明：本文涉及的资讯、数据等内容来自网络公共信息，我们对以上信息的准确性和完整性不做任何保证，涉及的信息随时都可能会有新变化，仅作为学习交流之用。', font: '12px sans-serif' } }
          ]
      }
    },
    downloadTableImage(){
        try{
            const showL2 = this.hasLevel2
            const showL3 = this.hasLevel3
            const showQ = this.showQuotes
            
            // 动态构建表头和列宽
            const header = []
            const colWidths = []
            header.push('一级大类'); colWidths.push(110)
            if (showL2) { header.push('二级小类'); colWidths.push(110) }
            if (showL3) { header.push('三级细分'); colWidths.push(110) }
            header.push('个股'); colWidths.push(110)
            if (showQ) {
              header.push('股票代码'); colWidths.push(85)
              header.push('流通市值'); colWidths.push(85)
              header.push('当前价格'); colWidths.push(70)
              header.push('涨跌幅'); colWidths.push(70)
              header.push('10日涨幅'); colWidths.push(70)
              header.push('20日涨幅'); colWidths.push(70)
            }
            header.push('相关性'); colWidths.push(450)
            if (this.canEdit) {
              header.push('信息源'); colWidths.push(160)
            }

            const paddingX = 10
            const paddingY = 8
            const font = '12px sans-serif'
            const rows = Array.isArray(this.mergedRows) ? this.mergedRows : []
            const ctx = document.createElement('canvas').getContext('2d')
            ctx.font = font
            const wrap = (text, maxWidth) => {
              const t = String(text || '')
              const lines = []
              const measure = (s) => ctx.measureText(s).width
              
              // 简单处理换行
              const paragraphs = t.split('\n')
              paragraphs.forEach(p => {
                const words = p.split(/\s+/)
                let line = ''
                for (let i=0;i<words.length;i++){
                  const w = words[i]
                  const test = line ? (line + ' ' + w) : w
                  if (measure(test) + paddingX*2 <= maxWidth) { line = test } 
                  else {
                    if (line) lines.push(line)
                    if (measure(w) + paddingX*2 <= maxWidth) { line = w } 
                    else {
                      let part = ''
                      for (let j=0;j<w.length;j++){
                        const test2 = part + w[j]
                        if (measure(test2) + paddingX*2 <= maxWidth) part = test2
                        else { if (part) lines.push(part); part = w[j] }
                      }
                      line = part
                    }
                  }
                }
                if (line) lines.push(line)
              })
              return lines.length ? lines : ['']
            }
            
            // 动态索引计算
            let currentIdx = 0
            const IDX = { L1: currentIdx++ }
            if (showL2) IDX.L2 = currentIdx++
            if (showL3) IDX.L3 = currentIdx++
            IDX.STOCK = currentIdx++
            if (showQ) {
              IDX.CODE = currentIdx++
              IDX.CAP = currentIdx++
              IDX.PRICE = currentIdx++
              IDX.RATE = currentIdx++
              IDX.RATE_10D = currentIdx++
              IDX.RATE_20D = currentIdx++
            }
            IDX.REL = currentIdx++
            if (this.canEdit) {
              IDX.SRC = currentIdx++
            }

            const colCount = header.length
            const tableWidth = colWidths.reduce((a,b)=>a+b,0)
            const rowBaseHeight = 26
            const rowHeights = []
            for (let i=0;i<rows.length;i++){
              const r = rows[i]
              const relLines = wrap(r['相关性'], colWidths[IDX.REL])
              let srcLines = ['']
              if (this.canEdit) {
                srcLines = wrap(r['信息源'], colWidths[IDX.SRC])
              }
              const lh = Math.max(relLines.length, srcLines.length, 1)
              rowHeights.push(Math.max(rowBaseHeight, lh * 18))
            }
            // 计算声明文字高度
            const disclaimer = '声明：本文涉及的资讯、数据等内容来自网络公共信息，我们对以上信息的准确性和完整性不做任何保证，涉及的信息随时都可能会有新变化，仅作为学习交流之用。'
            const discLines = wrap(disclaimer, tableWidth - paddingX * 2)
            const disclaimerHeight = discLines.length * 18
            
            let totalHeight = 65 + disclaimerHeight + 10 // 顶部间距 + 声明高度 + 间隙
            for (let i=0;i<rowHeights.length;i++) totalHeight += rowHeights[i]
            totalHeight += 40
            
            const canvas = document.createElement('canvas')
            canvas.width = tableWidth + 2
            canvas.height = totalHeight
            const c = canvas.getContext('2d')
            c.fillStyle = '#ffffff'
            c.fillRect(0,0,canvas.width,canvas.height)
            
            // 水印
            c.save()
            c.globalAlpha = 0.08
            c.fillStyle = '#808080'
            c.textAlign = 'center'
            c.textBaseline = 'middle'
            c.font = 'bold 50px sans-serif'
            c.fillText('自在量化', canvas.width/2, canvas.height/2 - 20)
            c.font = '24px sans-serif'
            c.fillText('quant.zizizaizai.com', canvas.width/2, canvas.height/2 + 20)
            c.restore()
            
            // 绘制标题
            c.fillStyle = '#333333'
            c.font = 'bold 16px sans-serif'
            c.textAlign = 'left'
            c.fillText(this.editForm.name || '题材表格', paddingX, 25)
            
            c.font = '12px sans-serif'
            c.fillStyle = '#999999'
            const tsStr = this.lastQuoteTime || this.editForm.updated_time || ''
            if(tsStr) c.fillText('更新时间：' + tsStr, paddingX, 45)
            
            // 绘制声明
            c.fillStyle = '#666666'
            let dy = 65
            for(let i=0; i<discLines.length; i++) {
              c.fillText(discLines[i], paddingX, dy)
              dy += 18
            }
            
            let y = dy + 10 // 表头开始位置
            c.strokeStyle = '#e0e0e0'
            c.lineWidth = 1
            c.font = 'bold 13px sans-serif'
            let x = 0
            for (let ci=0;ci<colCount;ci++){
              c.fillStyle = '#f5f5f5'
              c.fillRect(x, y, colWidths[ci], 40)
              c.strokeRect(x, y, colWidths[ci], 40)
              c.fillStyle = '#333333'
              c.textAlign = 'left'
              c.textBaseline = 'middle'
              c.fillText(header[ci], x + paddingX, y + 20)
              x += colWidths[ci]
            }
            y += 40
            
            const colX = []
            x = 0
            for (let ci=0;ci<colCount;ci++){ colX.push(x); x += colWidths[ci] }
            
            const groupHeightSum = (start, span) => {
              let h = 0
              for (let k=0;k<span;k++) h += rowHeights[start+k]
              return h
            }
            
            for (let ri=0;ri<rows.length;ri++){
              const r = rows[ri]
              const h = rowHeights[ri]
              
              // 绘制普通列网格
              for (let ci=IDX.STOCK;ci<colCount;ci++){
                c.strokeRect(colX[ci], y, colWidths[ci], h)
              }
              
              // 绘制合并列
              const drawMerged = (ci, showFlag, spanProp, textKey) => {
                if (ci === undefined) return
                if (!r[showFlag]) return
                const cx = colX[ci]
                const span = r[spanProp] || 1
                const mh = groupHeightSum(ri, span)
                c.strokeRect(cx, y, colWidths[ci], mh)
                c.textAlign = 'left'
                c.textBaseline = 'top'
                c.fillStyle = '#333333'
                const prevFont = c.font
                c.font = 'bold 12px sans-serif'
                const lines = wrap(r[textKey], colWidths[ci])
                let ty = y + paddingY
                for (let li=0;li<lines.length && li<20;li++){
                  c.fillText(lines[li], cx + paddingX, ty)
                  ty += 16
                }
                c.font = prevFont
              }
              
              drawMerged(IDX.L1, '_l1Show', '_l1Span', '一级大类')
              if(showL2) drawMerged(IDX.L2, '_l2Show', '_l2Span', '二级小类')
              if(showL3) drawMerged(IDX.L3, '_l3Show', '_l3Span', '三级细分')
              
              // 绘制个股
              c.textAlign = 'left'
              c.textBaseline = 'top'
              c.fillStyle = '#333333'
              c.font = 'bold 12px sans-serif'
              c.fillText(String(r['个股'] || ''), colX[IDX.STOCK] + paddingX, y + paddingY)
              c.font = font
              
              // 绘制行情
              if (showQ) {
                const code = r.symbol_code || r['股票代码'] || ''
                c.fillText(String(code), colX[IDX.CODE] + paddingX, y + paddingY)
                
                const cap = r['流通市值']
                const capStr = (cap !== undefined && cap !== null && cap !== '') ? this.formatYi(cap) : ''
                c.fillText(capStr, colX[IDX.CAP] + paddingX, y + paddingY)
                
                const price = r['最新价']
                c.fillText(price !== undefined ? String(price) : '', colX[IDX.PRICE] + paddingX, y + paddingY)
                
                const rate = r['涨跌幅']
                if (rate !== undefined && rate !== null && rate !== '') {
                  const rv = parseFloat(rate)
                  if (rv > 0) c.fillStyle = '#e33e33'
                  else if (rv < 0) c.fillStyle = '#17803d'
                  else c.fillStyle = '#333333'
                  c.fillText(String(rate) + '%', colX[IDX.RATE] + paddingX, y + paddingY)
                }
                
                const rate10d = r['10日涨幅']
                if (rate10d !== undefined && rate10d !== null && rate10d !== '') {
                  const rv = parseFloat(rate10d)
                  if (rv > 0) c.fillStyle = '#e33e33'
                  else if (rv < 0) c.fillStyle = '#17803d'
                  else c.fillStyle = '#333333'
                  c.fillText(String(rate10d) + '%', colX[IDX.RATE_10D] + paddingX, y + paddingY)
                }

                const rate20d = r['20日涨幅']
                if (rate20d !== undefined && rate20d !== null && rate20d !== '') {
                  const rv = parseFloat(rate20d)
                  if (rv > 0) c.fillStyle = '#e33e33'
                  else if (rv < 0) c.fillStyle = '#17803d'
                  else c.fillStyle = '#333333'
                  c.fillText(String(rate20d) + '%', colX[IDX.RATE_20D] + paddingX, y + paddingY)
                }
              }
              
              // 绘制相关性和信息源
              c.fillStyle = '#333333'
              const relLines = wrap(r['相关性'], colWidths[IDX.REL])
              let ty1 = y + paddingY
              for (let li=0;li<relLines.length && li<40;li++){
                c.fillText(relLines[li], colX[IDX.REL] + paddingX, ty1)
                ty1 += 16
              }
              
              const srcLines = wrap(r['信息源'], colWidths[IDX.SRC])
              let ty2 = y + paddingY
              for (let li=0;li<srcLines.length && li<40;li++){
                c.fillText(srcLines[li], colX[IDX.SRC] + paddingX, ty2)
                ty2 += 16
              }
              
              y += h
            }
            
            const a = document.createElement('a')
            const fileName = (this.editForm.name || '题材表格')
            const ts = this.lastQuoteTime || this.editForm.updated_time || this.formatNow()
            a.href = canvas.toDataURL('image/png')
            a.download = `${fileName}-${ts}.png`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }catch(e){
            alert('生成图片失败')
            console.error(e)
        }
    },
    downloadTableCsv(){
      const rows = Array.isArray(this.editForm.rows) ? this.editForm.rows : []
      const showQ = this.showQuotes
      const header = showQ
        ? ['一级大类','二级小类','三级细分','个股','股票代码','流通市值','当前价格','涨跌幅','10日涨幅','20日涨幅','相关性']
        : ['一级大类','二级小类','三级细分','个股','相关性']
      if (this.canEdit) {
        header.push('信息源')
      }
      const lines = [header.join(',')]
      rows.forEach(row => {
        const cap = showQ ? this.formatYi(row['流通市值']) : ''
        const price = showQ ? (row['最新价'] !== undefined && row['最新价'] !== null ? row['最新价'] : '') : ''
        const rate = showQ && row['涨跌幅'] !== undefined && row['涨跌幅'] !== null ? `${row['涨跌幅']}%` : ''
        const rate10d = showQ && row['10日涨幅'] !== undefined && row['10日涨幅'] !== null ? `${row['10日涨幅']}%` : ''
        const rate20d = showQ && row['20日涨幅'] !== undefined && row['20日涨幅'] !== null ? `${row['20日涨幅']}%` : ''
        const code = showQ ? (row.symbol_code || row['股票代码'] || '') : ''
        const vals = showQ 
          ? [ row['一级大类'] || '', row['二级小类'] || '', row['三级细分'] || '', row['个股'] || '', code, cap, price, rate, rate10d, rate20d, row['相关性'] || '' ]
          : [ row['一级大类'] || '', row['二级小类'] || '', row['三级细分'] || '', row['个股'] || '', row['相关性'] || '' ]
        
        if (this.canEdit) {
          vals.push(row['信息源'] || '')
        }
        const escaped = vals.map(v => `"${String(v).replace(/"/g, '""')}"`)
        lines.push(escaped.join(','))
      })
      const csv = '\uFEFF' + lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const name = this.editForm.name || '题材表格'
      const ts = this.lastQuoteTime || this.editForm.updated_time || this.formatNow()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${name}-${ts}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
    },
    getRateStyle(rate) {
        if (rate === undefined || rate === null) return {};
        const r = parseFloat(rate);
        if (isNaN(r)) return {};
        if (r > 9) return { color: 'red', fontWeight: 'bold' };
        if (r > 5) return { color: 'red', fontWeight: 'bold' };
        if (r > 0) return { color: 'red' };
        if (r < 0) return { color: 'green' };
        return {};
    },
    openSidebar(item, dateStr = '') {
        if (!item || !item.symbol_code) { return }

        this.sidebarSymbolCode = item.symbol_code
        this.sidebarSymbolName = item['个股']
        this.sidebarDateMark = dateStr
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    },
    onChartClick(params) {
      if (params.componentType === 'markPoint') {
        const name = params.name
        const code = this.stockNameCodeMap[name]
        if (code) {
          // 获取 markPoint 所在的日期坐标
          const date = params.data.coord[0]
          this.openSidebar({ '个股': name, symbol_code: code }, date)
        }
      } else if (params.name) {
        // 如果点击的是 K 线或其它数据点，则将该日期设为基准日期
        const isDate = /^\d{4}-\d{2}-\d{2}$/.test(params.name)
        if (isDate) {
          this.comparisonBaseDate = params.name
          this.onComparisonBaseDateChange()
        }
      }
    },
    onStockLabelPositionChange(val) {
      localStorage.setItem('topic_kline_label_pos', val)
      this.generateKlineOption()
    },
    onStockLabelModeChange(val) {
      localStorage.setItem('topic_kline_label_mode', val)
      this.generateKlineOption()
    },
    onStockLabelLimitChange(val) {
      localStorage.setItem('topic_kline_label_limit', val)
      this.generateKlineOption()
    },
    onComparisonStockLimitChange(val) {
      localStorage.setItem('topic_comparison_stock_limit', val)
      this.fetchComparisonKlines()
    },
    setComparisonBaseRange(rangeType) {
      if (!this.klineData || this.klineData.length === 0) return
      
      const lastItem = this.klineData[this.klineData.length - 1]
      const lastDate = new Date(lastItem.trade_date)
      let targetDate = new Date(lastDate)
      
      if (rangeType === '1w') {
        targetDate.setDate(lastDate.getDate() - 7)
      } else if (rangeType === '1m') {
        targetDate.setMonth(lastDate.getMonth() - 1)
      } else if (rangeType === '3m') {
        targetDate.setMonth(lastDate.getMonth() - 3)
      } else if (rangeType === '6m') {
        targetDate.setMonth(lastDate.getMonth() - 6)
      } else if (rangeType === '1y') {
        targetDate.setFullYear(lastDate.getFullYear() - 1)
      } else if (rangeType === 'all') {
        this.comparisonBaseDate = this.klineData[0].trade_date
        this.onComparisonBaseDateChange()
        return
      }
      
      const targetStr = targetDate.toISOString().split('T')[0]
      
      let bestItem = this.klineData[0]
      for (let i = 0; i < this.klineData.length; i++) {
        const item = this.klineData[i]
        if (item.trade_date >= targetStr) {
          bestItem = item
          break
        }
      }
      
      this.comparisonBaseDate = bestItem.trade_date
      this.onComparisonBaseDateChange()
    },
    setDefaultComparisonBaseDate() {
      if (!this.klineData || this.klineData.length === 0) return
      
      const lastItem = this.klineData[this.klineData.length - 1]
      const lastDate = new Date(lastItem.trade_date)
      const targetDate = new Date(lastDate)
      targetDate.setDate(lastDate.getDate() - 7)
      const targetStr = targetDate.toISOString().split('T')[0]
      
      let bestItem = this.klineData[0]
      for (let i = 0; i < this.klineData.length; i++) {
        const item = this.klineData[i]
        if (item.trade_date >= targetStr) {
          bestItem = item
          break
        }
      }
      this.comparisonBaseDate = bestItem.trade_date
    },
    onComparisonBaseDateChange() {
      this.generateKlineOption()
      if (this.showStockComparison) {
        this.fetchComparisonKlines()
      }
    }
  }
}
</script>

<style scoped>
.topic-detail-container {
  padding: 0.1rem 1rem;
}

.table-responsive {
  border-radius: 4px;
  overflow-x: auto;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}

.wonder-table-view-small th {
  background-color: #f8f9fa;
  font-weight: 600;
  vertical-align: middle;
  text-align: center;
  border-bottom: 2px solid #dee2e6;
}

body.theme-dark .wonder-table-view-small th {
  background-color: var(--bg-muted);
  color: var(--text-main);
  border-bottom: 2px solid var(--border-color);
}

/* 订阅遮罩层美化 */
.subscription-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.6);
  border-radius: 4px;
}

body.theme-dark .subscription-overlay {
  background: rgba(15, 23, 42, 0.7);
}

.overlay-card {
  max-width: 400px;
  padding: 2.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.theme-dark .overlay-card {
  background: #1e293b;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
}

.lock-icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: #3b82f6;
}

.overlay-card h5 {
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1e293b;
}

body.theme-dark .overlay-card h5 {
  color: #f1f5f9;
}

.overlay-card p {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

body.theme-dark .overlay-card p {
  color: #94a3b8;
}

.instruction-link a {
  transition: all 0.2s ease;
}

.instruction-link a:hover {
  color: #2563eb !important;
  text-decoration: none !important;
}

body.theme-dark .wonder-table-view-small td {
  border-color: var(--border-color);
}

body.theme-dark .bg-light {
    background-color: var(--bg-card) !important;
}

/* 表格列宽控制 */
.wonder-table-view-small th.col-cat { width: 80px; }
.wonder-table-view-small th.col-stock { width: 80px; }
.wonder-table-view-small th.col-info { width: 100px; }
.wonder-table-view-small th.col-quote { width: 70px; }
.wonder-table-view-small td.col-content { 
  white-space: pre-wrap; 
  word-break: break-word; 
  min-width: 300px; 
}

@media (max-width: 768px) {
  .topic-detail-container { padding: 0.5rem; }
  .wonder-table-view-small th.col-cat { width: 30px; font-size: 10px; }
  .wonder-table-view-small th.col-stock { width: 60px; font-size: 10px; }
  .wonder-table-view-small th.col-quote { width: 50px; font-size: 10px; }
  .wonder-table-view-small td.col-content { min-width: 150px; }
  
  /* 移动端细化调整 */
  .wonder-table-view-small th, 
  .wonder-table-view-small td {
    padding: 3px 2px !important;
    font-size: 10px !important;
  }
}
.subscription-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(4px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-mode .subscription-overlay {
  background: rgba(30, 30, 30, 0.7);
}

.overlay-content {
  text-align: center;
  background: var(--el-bg-color, #fff);
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

.dark-mode .overlay-content {
  background: #2d2d2d;
}

.lock-icon {
  font-size: 48px;
  color: #e6a23c;
  margin-bottom: 20px;
}

.overlay-content h3 {
  margin-bottom: 12px;
  color: var(--el-text-color-primary, #303133);
}

.overlay-content p {
  color: var(--el-text-color-secondary, #909399);
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.6;
}

.chart-blurred {
  filter: blur(8px);
  pointer-events: none;
}
</style>
